import { DroppedImage , markStore , marksData , MarkStore } from "@lib/store/src/mark";
import { showConnectionLine , removeConnectionLine , startDragConnection , handleImageDrop, removeConnectionLineForce, findElementInSameWindow } from "./event-handlers";
import { showContextMenu , showGroupContextMenu } from "./context-menu";
import { runInAction } from "mobx";
import { showHideEyeIndicator } from "./context-menu-mark";
import i18next from "i18next";

function getAllDroppedImages(): DroppedImage[] {
  const all: DroppedImage[] = [];
  (marksData.marksData as MarkStore[]).forEach((store) => {
    if (store && Array.isArray(store.droppedImages)) {
      all.push(...store.droppedImages);
    }
  });
  return all;
}

function findImageAndStoreById(id: string): { store: MarkStore; img: DroppedImage } | null {
  for (const store of marksData.marksData as MarkStore[]) {
    if (!store || !Array.isArray(store.droppedImages)) continue;
    const img = store.droppedImages.find(i => i.id === id);
    if (img) return { store, img };
  }
  return null;
}

function isDeletedGlobal(img: DroppedImage): boolean {
  const pair = findImageAndStoreById(img.id);
  if (!pair) return false;
  return pair.store.isDeleted(img);
}

export function getStoreByControlId(id: string): MarkStore | undefined {
  return marksData.marksData.find(store => store.id === id);
}

export function createImageElement(data: DroppedImage): HTMLImageElement {
  const img = document.createElement("img");
  img.onload = () => {
    img.dataset.origWidth = img.width.toString();
    img.dataset.origHeight = img.height.toString();
  }
  img.src = data.src;
  img.setAttribute('data-id',data.id);
  if(data.windowId) {
    img.dataset.windowId = String(data.windowId)
  }
  if(data.group) {
    img.setAttribute('data-group', data.group)
  }
  if(data.origin) {
    img.setAttribute('origin', data.origin)
  }

  if(data.controlId) img.setAttribute("data-control-id", data.controlId)


  Object.assign(img.style, {
    position: "absolute",
    left: `${data.left ?? data.x}px`,
    top: `${data.top ?? data.y}px`,
    width: data.width ? `${data.width}px` : "200px",
    height: data.height ? `${data.height}px` : "auto",
    pointerEvents: "auto",
    userSelect: "none",
    zIndex: 99999999999,
  });

  img.classList.add("dropped-image");
  updateImageTooltip(img, data)
  return img;
}

export function wrapImage(img: HTMLImageElement, imgData: DroppedImage) {
  const wrapper = document.createElement("div");
  wrapper.className = "image-container";
  wrapper.style.position = "absolute";
  wrapper.style.pointerEvents = "auto";
  img.classList.add("dropped-image");
  if(imgData.windowId) {
    wrapper.dataset.windowId = String(imgData.windowId)
  }
  wrapper.appendChild(img);
  return wrapper;
}

export function getHostWindowId(hostElement: HTMLElement): string | null {
  const hostWin = hostElement.closest("ms-window") as HTMLElement | null;
  return hostWin?.getAttribute("data-window-id") ?? hostWin?.id ?? null;
}

export function restoreImagesFromStore(hostElement: HTMLElement) {
  const controlId = hostElement.getAttribute("id");
  const hostWin = hostElement.closest("ms-window") as HTMLElement | null;
  const hostWindowId =
    hostWin?.getAttribute("data-window-id")
    ?? hostWin?.id
    ?? null;
  if (!controlId) return;
  const store = getStoreByControlId(controlId);
  if (!store) return;
  store.droppedImages.forEach(imgData => {
  if (imgData.windowId !== hostWindowId) return;

  const els = document.querySelectorAll(`img.dropped-image[data-id="${imgData.id}"]`);

  els.forEach(el => {
    const win = el.closest("ms-window");
    const winId = win?.getAttribute("data-window-id") ?? win?.id ?? null;
    if (winId !== hostWindowId) return;
    const wrapper = el.closest(".image-container");
    if (wrapper) wrapper.remove();
    else el.remove();
  });
});

  store.droppedImages.sort((a, b) => {
    if (b.group && a.group) {
      if (b.priority !== a.priority) return b.priority - a.priority;
      return b.timestamp - a.timestamp;
    }
    return 0;
  });

  store.droppedImages.forEach(imgData => {
    if(imgData.windowId !== hostWindowId) return;
    if (store.isDeleted(imgData)) return;
    const isHidden = imgData.hidden === true;

    if (!imgData.origin) imgData.origin = hostElement.hasAttribute("marks") ? "mark" : "poster";
    const img = createImageElement(imgData);
    if (imgData.group) {
      const groupItems = store.droppedImages.filter(i => i.group === imgData.group);
      const index = groupItems.findIndex(i => i.id === imgData.id);
      img.style.zIndex = `${2000 + index}`;
    }

    if(imgData.targetSide) {
      img.dataset.targetSide = imgData.targetSide;
    }
    if(imgData.linkSide) {
      img.dataset.linkSide = imgData.linkSide;
    }
    img.setAttribute("data-control-id", controlId);
    img.style.visibility = isHidden ? "hidden" : "visible";
    if(isHidden) showHideEyeIndicator(controlId);

    makeImageDraggable(img, imgData , store);
    attachImageLogic(img, imgData, selectImage, hostElement);
    updateImageTooltip(img, imgData)
    if (imgData.linkedElementId) {
      const el = document.getElementById(imgData.linkedElementId);
      if (el) {
        if (imgData.propertysubtypes && imgData.setvalue) {
          const subtype = imgData.propertysubtypes.toLowerCase();
          const val = imgData.setvalue;
          if (el[subtype] !== null) {
            el.attributeChangedCallback(subtype, el[subtype], val, null, {forced: true});
          }
        }
      }
    }
    if (imgData.group) {
      img.setAttribute("data-group", imgData.group);
    }
    checkForOverlaps(imgData)
  });
}


export function getSafeClientPoint(e: any, img?: HTMLElement) {
  let x = e?.clientX;
  let y = e?.clientY;
  if (Number.isFinite(x) && Number.isFinite(y)) return { x, y };
  if (img) {
    const r = img.getBoundingClientRect();
    x = r.left + r.width / 2;
    y = r.top + r.height / 2;
    if (Number.isFinite(x) && Number.isFinite(y)) return { x, y };
  }
  x = window.innerWidth / 2;
  y = window.innerHeight / 2;
  if (Number.isFinite(x) && Number.isFinite(y)) return { x, y };
  return null;
}

export function updateLastModify(img: DroppedImage, user: string) {
  img.commentAuthor = user;
  img.commentDate = new Date().toISOString();
}

export function makeImageDraggable(
  img: HTMLImageElement,
  imgData: DroppedImage,
  store: MarkStore
) {
  let isDragging = false;
  let offsetX = 0;
  let offsetY = 0;

  let isResizing = false;
  let resizeDir: "right" | "bottom" | null = null;
  const resizeMargin = 5;

  img.addEventListener("mousedown", (e) => {
    const rect = img.getBoundingClientRect();
    const nearRight = e.clientX >= rect.right - resizeMargin;
    const nearBottom = e.clientY >= rect.bottom - resizeMargin;

    if (nearRight || nearBottom) {
      isResizing = true;
      resizeDir = nearRight ? "right" : "bottom";
      e.preventDefault();
      return;
    }

    if (e.button !== 0) return;

    if (imgData.group) {
      runInAction(() => {
        imgData.group = null;
      });
    }
    img.removeAttribute("data-group");

    isDragging = true;
    (img as any)._isDragging = true;
    const win = img.closest("ms-window") as HTMLElement | null;
    if (win) {
      const winRect = win.getBoundingClientRect();
      const scale = winRect.width / win.offsetWidth || 1;

      const localMouseX = (e.clientX - winRect.left) / scale;
      const localMouseY = (e.clientY - winRect.top) / scale;
      const curLeft = img.offsetLeft;
      const curTop  = img.offsetTop;

      offsetX = localMouseX - curLeft;
      offsetY = localMouseY - curTop;
    } else {
      const imgRect = img.getBoundingClientRect();
      offsetX = (e.clientX - imgRect.left);
      offsetY = (e.clientY - imgRect.top);
    }

    e.preventDefault();
  });

  document.addEventListener("mousemove", (e) => {
    if (isDragging) {
      const win = img.closest("ms-window");
      const winRect = img.closest("ms-window").getBoundingClientRect()
      const scale = winRect.width / win.offsetWidth;
      let newLeft = (e.clientX - winRect.left) / scale - offsetX;
      let newTop  = (e.clientY - winRect.top)  / scale - offsetY;
      const maxLeft = (winRect.width  / scale) - img.offsetWidth;
      const maxTop  = (winRect.height / scale) - img.offsetHeight;
      newLeft = Math.max(0, Math.min(newLeft, maxLeft));
      newTop  = Math.max(0, Math.min(newTop,  maxTop));

      img.style.left = `${newLeft}px`;
      img.style.top  = `${newTop}px`;
      if ((img as any)._forceShowConnection || (img as any)._hoverConnection) {
        showConnectionLine(img, imgData);
      }
      document.querySelectorAll(`.anchor-point[data-owner-img-id="${imgData.id}"]`).forEach(el => el.remove());
      }

    if (isResizing && resizeDir) {
      const rect = img.getBoundingClientRect();

      const origW = parseFloat(img.dataset.origWidth || img.width.toString());
      const origH = parseFloat(img.dataset.origHeight || img.height.toString());
      const ratio = origW / origH;

      const win = img.closest("ms-window") as HTMLElement;
      const winRect = win.getBoundingClientRect();
      const scale = winRect.width / win.offsetWidth || 1;

      const localX = (e.clientX - winRect.left) / scale;
      const localY = (e.clientY - winRect.top)  / scale;

      const imgLeft = img.offsetLeft;
      const imgTop  = img.offsetTop;

      if (resizeDir === "right") {
        const newWidth = localX - imgLeft;
        if (newWidth > 20) {
          const newHeight = newWidth / ratio;
          img.style.width  = `${newWidth}px`;
          img.style.height = `${newHeight}px`;
        }
      }

      else if (resizeDir === "bottom") {
        const newHeight = localY - imgTop;
        if (newHeight > 20) {
          const newWidth = newHeight * ratio;
          img.style.width  = `${newWidth}px`;
          img.style.height = `${newHeight}px`;
        }
      }

      else if (resizeDir === "corner") {
        const newWidth  = localX - imgLeft;
        const newHeight = localY - imgTop;

        let finalW = newWidth;
        let finalH = newWidth / ratio;

        if (finalH > newHeight) {
          finalH = newHeight;
          finalW = newHeight * ratio;
        }

        if (finalW > 20 && finalH > 20) {
          img.style.width  = `${finalW}px`;
          img.style.height = `${finalH}px`;
        }
      }
    }
  });

  document.addEventListener("mouseup", (e) => {
    if (isDragging || isResizing) {
      isDragging = false;
      (img as any)._isDragging = false;
      isResizing = false;
      resizeDir = null;
      const item = store.droppedImages.find(i => i.id === imgData.id);
      if (item) {
        const left = img.offsetLeft;
        const top = img.offsetTop;
        runInAction(() => {
          item.left = left;
          item.top = top;
          item.x = left;
          item.y = top;
          item.width = img.offsetWidth;
          item.height = img.offsetHeight;
          item.group = img.getAttribute("data-group") || null;
          const user = $sw.serverState.UserData.user || "";
          updateLastModify(item, user)
          const imgEl = document.querySelector(`img[data-id="${item.id}"]`)
          if(imgEl) updateImageTooltip(imgEl, item);
        })
      }
      checkForOverlaps(imgData);
    }
    if (!(img as any)._forceShowConnection) {

      const pt = getSafeClientPoint(e, img);
      if (!pt) return;
      const under = document.elementFromPoint(pt.x, pt.y) as HTMLElement | null;
      const stillOverThis = !!under && !!under.closest(`.image-container img.dropped-image[data-id="${imgData.id}"]`);
      if (!stillOverThis) {
        (img as any)._hoverConnection = false;
        removeConnectionLine(img);
      }
    }
  });

  img.addEventListener("mousemove", (e) => {
    const rect = img.getBoundingClientRect();
    const nearRight = e.clientX >= rect.right - resizeMargin;
    const nearBottom = e.clientY >= rect.bottom - resizeMargin;

    if (nearRight && nearBottom) img.style.cursor = "nwse-resize";
    else if (nearRight) img.style.cursor = "ew-resize";
    else if (nearBottom) img.style.cursor = "ns-resize";
    else img.style.cursor = "default";
  });
}

export function checkForOverlaps(movedImage: DroppedImage) {
  const movedEl = document.querySelector(`.dropped-image[data-id="${movedImage.id}"]`) as HTMLElement | null;
  if (!movedEl) return;

  const movedRect = movedEl.getBoundingClientRect();
  const allImages = getAllDroppedImages();

  const overlapping = allImages.filter(img => {
    if (img.id === movedImage.id || isDeletedGlobal(img)) return false;
    const el = document.querySelector(`img[data-id="${img.id}"]`) as HTMLElement;
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    const overlap = !(movedRect.right < rect.left || movedRect.left > rect.right || movedRect.bottom < rect.top || movedRect.top > rect.bottom);
    return overlap;
  });
  if ((movedImage.origin === "poster" && movedImage.linkedElementId) || overlapping.some(img => img.origin === "poster" && img.linkedElementId)) {
    if (movedImage.group) {
      runInAction(() => movedImage.group = null);
      movedEl.removeAttribute("data-group");
    }
    overlapping.forEach(img => {
      if (img.group) {
        const el = document.querySelector(`img[data-id="${img.id}"]`);
        if (el) el.removeAttribute("data-group");
        runInAction(() => img.group = null);
      }
    });
    return; 
  }
  if (overlapping.length === 0) {
    runInAction(() => {
      movedImage.group = null
    });
    movedEl.removeAttribute("data-group");
    return;
  }
  const isSingle = (img) => (img.priority ?? 0) < 1;

  if (isSingle(movedImage)) {
    showTemporaryTooltip(movedEl , `${i18next.t("installed_individually")}`);
    runInAction(() => {(movedImage.group = null)});
    movedEl.removeAttribute("data-group");
    return;
  }

  if (overlapping.some(isSingle)) {
    showTemporaryTooltip(movedEl,`${i18next.t("installed_individually")}`);
    runInAction(() => {(movedImage.group = null)});
    movedEl.removeAttribute("data-group");
    return;
  }
  let groupId = movedImage.group || overlapping.find(img => img.group)?.group || `group-${crypto.randomUUID()}`;
  runInAction(() => {
    movedImage.group = groupId;
  });
  
  movedEl.setAttribute("data-group", groupId);
  overlapping.forEach(img => {
    const el = document.querySelector(`img[data-id="${img.id}"]`);
    if (el) el.setAttribute("data-group", groupId);
    runInAction(() => (img.group = groupId));
  });

  const groupEls = Array.from(document.querySelectorAll(`img[data-group="${groupId}"]`)) as HTMLElement[];
  let offset = 8;
  let baseLeft = Infinity;
  let baseTop = Infinity;
  groupEls.forEach(el => {
    const r = el.getBoundingClientRect();
    baseLeft = Math.min(baseLeft, r.left);
    baseTop  = Math.min(baseTop,  r.top);
  });

  const groupData = groupEls.map(el => {
    const id = el.dataset.id!;
    const pair = findImageAndStoreById(id);
    return {
      el,
      img: pair?.img,
      priority: pair?.img?.priority ?? 0,
      timestamp: pair?.img?.timestamp ?? 0
    };
  });
  const linked = groupData.filter(it =>  it.img?.linkedElementId);
  const unlinked = groupData.filter(it => !it.img?.linkedElementId);

  if (linked.length > 0 && unlinked.length > 0) {
    linked.sort((a, b) => {
      if (b.priority !== a.priority) return b.priority - a.priority;
      return b.timestamp - a.timestamp;
    });
    unlinked.sort((a, b) => b.timestamp - a.timestamp);
    groupData.length = 0;
    groupData.push(...linked, ...unlinked);
  } else {
    groupData.sort((a, b) => {
      if (b.priority !== a.priority) return b.priority - a.priority;
      return b.timestamp - a.timestamp;
    });
  }
    const anyEl = groupEls[0];
    const win = anyEl?.closest("ms-window") as HTMLElement | null;

    if (win) {
      const winRect = win.getBoundingClientRect();
      const scale = winRect.width / win.offsetWidth || 1;

      baseLeft = (baseLeft - winRect.left) / scale;
      baseTop  = (baseTop  - winRect.top)  / scale;
      offset = offset / scale;
    }

  groupData.forEach((item, index) => {
    const realIndex = groupData.length - 1 - index;
    const left = baseLeft + offset * realIndex;
    const top  = baseTop  + offset * realIndex;
    item.el.style.left = `${left}px`;
    item.el.style.top  = `${top}px`;
    item.el.style.zIndex = `${2000 + realIndex}`;

    if (item.img) {
        runInAction(() => {
            item.img.left = left;
            item.img.top = top;
            item.img.x = left;
            item.img.y = top;
        });
    }
  });
}

let activeClickImage: HTMLElement | null = null;

export function attachImageLogic(
  img: HTMLImageElement,
  imgData: DroppedImage,
  selectImage: (img: HTMLElement , wrapper: HTMLElement , imgData: DroppedImage, controlId: string) => void,
  hostElement: HTMLElement
) {
    const hostControlId = hostElement.getAttribute("id") || undefined;
    const controlId = imgData.controlId || hostControlId || undefined;
    
    if (!imgData.controlId && controlId) {
      imgData.controlId = controlId;
    }
    const wrapper = wrapImage(img, imgData);
    img.setAttribute("data-id", imgData.id);
    img.dataset.id = imgData.id;

    if (controlId) {
      img.setAttribute("data-control-id", controlId);
      wrapper.setAttribute("data-control-id", controlId);
    }

    if (imgData.group) {
      img.setAttribute("data-group", imgData.group)
    }
    
    img.addEventListener("mouseenter", () => {
      const store = getStoreByControlId(controlId);
      const realImg = store?.droppedImages.find(i => i.id === imgData.id);
      if($pm.hasPermissions && !($pm.checkAllow($pm.getEntryFromElement(hostElement,"ManageMarksAndPosters")))) return;
      if(!(img as any)._forceShowConnection) {
        (img as any)._hoverConnection = true;
        showConnectionLine(img, imgData);
      }
      if (realImg.linkedElementId) {
        const linked = realImg?.linkedElementId ? findElementInSameWindow(img, realImg.linkedElementId) : null;
        if (linked) {
          linked.style.outline = `2px solid ${store.selectionoutlinecolor}`;
          linked.style.outlineOffset = "2px";
        }
      }
    });

    img.addEventListener("click", (e) => {
        e.stopPropagation();
        if ($pm.hasPermissions && !($pm.checkAllow($pm.getEntryFromElement(hostElement,"ManageMarksAndPosters")))) 
            return;

        selectImage(img, wrapper, imgData, controlId);
        const store = getStoreByControlId(controlId);
        const realImg = store?.droppedImages.find(i => i.id === imgData.id);
        if (!realImg) return;
        const wasEnabled = (img as any)._forceShowConnection;
        const nowEnabled = !wasEnabled;
        (img as any)._forceShowConnection = nowEnabled;
        if (nowEnabled) {
          if (activeClickImage && activeClickImage !== img) {
            (activeClickImage as any)._forceShowConnection = false;
            (activeClickImage as any)._hoverConnection = false;
            removeConnectionLineForce(activeClickImage);
            const prevId = activeClickImage.dataset.id; 
            const prevControlId = activeClickImage.dataset.controlId; 
            if (prevId && prevControlId) { 
              const prevStore = getStoreByControlId(prevControlId); 
              const prevImg = prevStore?.droppedImages.find(i => i.id === prevId); 
              if (prevImg?.linkedElementId) { 
                const prevLinked = document.getElementById(prevImg.linkedElementId); 
                if (prevLinked) { 
                  prevLinked.style.outline = ""; 
                  prevLinked.style.outlineOffset = ""; 
                } 
              } 
            }
          }

          activeClickImage = img;
          (img as any)._forceShowConnection = true;
          (img as any)._hoverConnection = false;
          showConnectionLine(img, imgData);
          const realImg = store?.droppedImages.find(i => i.id === imgData.id);
          if(realImg.linkedElementId) {
            const linked = realImg?.linkedElementId ? findElementInSameWindow(img, realImg.linkedElementId) : null;
            if(linked) {
              linked.style.outline = `2px solid ${store.selectionoutlinecolor ?? "#216aff"}`;
              linked.style.outlineOffset = "2px";
            }
          }
        } else {
          (img as any)._forceShowConnection = false;
          (img as any)._hoverConnection = false;
          removeConnectionLineForce(img);

          if (activeClickImage === img) {
            activeClickImage = null;
          }
        }
    });

    img.addEventListener("mousemove", (e) => {
      const rect = img.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;

      const threshold = 20;
      let side: "top" | "bottom" | "left" | "right" | null = null;

      if (offsetY < threshold) side = "top";
      else if (offsetY > rect.height - threshold) side = "bottom";
      else if (offsetX < threshold) side = "left";
      else if (offsetX > rect.width - threshold) side = "right";

      if (side) showAnchorPoint(img, imgData, side , controlId);
    });

    wrapper.addEventListener("mouseleave", (e) => {
      if ((img as any)._isDragging) return;
      if((img as any)._forceShowConnection) return;
      const related = e.relatedTarget as HTMLElement;
      if (related?.classList.contains("anchor-point")) return;
      if((img as any)._hoverConnection) {
        (img as any)._hoverConnection = false;
        removeConnectionLine(img);
      }

      document.querySelectorAll(`.anchor-point[data-owner-img-id="${imgData.id}"]`).forEach(el => el.remove())
      const store = getStoreByControlId(imgData.controlId);
      const realImg = store?.droppedImages.find(i => i.id === imgData.id);
      if(realImg.linkedElementId) {
        const linked = realImg?.linkedElementId ? findElementInSameWindow(img, realImg.linkedElementId) : null;
        if(linked) {
          linked.style.outline = '';
          linked.style.outlineOffset = '';
        }
      }
    });

    img.addEventListener("contextmenu", (e) => {
      if($pm.hasPermissions && !($pm.checkAllow($pm.getEntryFromElement(hostElement,"ManageMarksAndPosters")))) return;
      e.preventDefault();

      const group = img.getAttribute("data-group");
      if (group) {
        showGroupContextMenu(e, group);
      } else {
        showContextMenu(img, imgData, e.clientX, e.clientY);
      }
    });
    document.addEventListener("click", (e: MouseEvent) => {    
      const target = e.target as HTMLElement;    
      const ownerImg = target.closest<HTMLImageElement>(`img.dropped-image[data-id="${imgData.id}"]`);
      if (ownerImg) return;

      const ownerAnchor = target.closest<HTMLElement>(`.anchor-point[data-owner-img-id="${imgData.id}"]`);
      if (ownerAnchor) return;
      const store = getStoreByControlId(controlId);    
      const realImg = store?.droppedImages.find(i => i.id === imgData.id);    
      (img as any)._forceShowConnection = false;      
      removeConnectionLineForce(img);    
      (img as any)._hoverConnection = false;
      if (realImg?.linkedElementId) {    
        const linked = realImg?.linkedElementId ? findElementInSameWindow(img, realImg.linkedElementId) : null;
        if (linked) {    
          linked.style.outline = "";    
          linked.style.outlineOffset = "";    
        }    
      }    
    });    
    const win = hostElement.closest("ms-window");
    if (win) {
        win.appendChild(wrapper);
    } else {
        document.body.appendChild(wrapper);
    }
}

function showAnchorPoint(img: HTMLImageElement, imgData: DroppedImage, side: "top" | "bottom" | "left" | "right", controlId: string) {
  document
  .querySelectorAll(`.anchor-point[data-owner-img-id="${imgData.id}"]`)
  .forEach(el => el.remove());
  const store = getStoreByControlId(controlId)
  const anchor = document.createElement("div");
  anchor.className = "anchor-point";
  anchor.dataset.id = imgData.id;
  anchor.dataset.side = side;
  anchor.style.position = 'absolute';
  anchor.style.border = `5px solid ${store.selectionoutlinecolor}`
  anchor.style.width = "5px";
  anchor.style.height = "5px";
  anchor.style.borderRadius = "50%";
  anchor.style.pointerEvents = "auto";
  anchor.dataset.ownerImgId = imgData.id;

  const positions = {
    top: () => {
      anchor.style.left = `${img.offsetLeft + img.offsetWidth / 2 - 5}px`;
      anchor.style.top = `${img.offsetTop - 10}px`;
    },
    bottom: () => {
      anchor.style.left = `${img.offsetLeft + img.offsetWidth / 2 - 5}px`;
      anchor.style.top = `${img.offsetTop + img.offsetHeight - 5}px`;
    },
    left: () => {
      anchor.style.left = `${img.offsetLeft - 10}px`;
      anchor.style.top = `${img.offsetTop + img.offsetHeight / 2 - 5}px`;
    },
    right: () => {
      anchor.style.left = `${img.offsetLeft + img.offsetWidth - 5}px`;
      anchor.style.top = `${img.offsetTop + img.offsetHeight / 2 - 5}px`;
    }
  };
  positions[side]();
  anchor.addEventListener("mousedown", (e) => startDragConnection(e, anchor, imgData));
  img.parentElement?.appendChild(anchor);
}

export function selectImage(img: HTMLImageElement, wrapper: HTMLElement, imgData: DroppedImage, controlId: string) {
  if (img.closest(".mark-modal")) return;
  const store = getStoreByControlId(controlId)
  document.querySelectorAll('.anchor-point').forEach((el) => el.remove())
  clearAllSelections();
  img.classList.add("selected");
  img.style.border = `3px solid ${store.selectionoutlinecolor}`
}

export function bindImageToElement(imgData: DroppedImage, el: any) {
  if (el.tagName.toLowerCase() === "ms-window") return;
  const store = getStoreByControlId(imgData.controlId);
  if (!store) return;
  
  const realImg = store.droppedImages.find(i => i.id === imgData.id);
  if (!realImg) return;
  const isSingle = (img: DroppedImage) => (img.priority ?? 0) < 1;
  const postersOnElement = store.droppedImages.filter(img => img.id !== realImg.id && img.linkedElementId === el.id);

  const hasAnyPosterOnElement = postersOnElement.length > 0;
  const hasSinglePosterOnElement = postersOnElement.some(isSingle);
  const isCurrentSingle = isSingle(realImg);

  if ((isCurrentSingle && hasAnyPosterOnElement) || (!isCurrentSingle && hasSinglePosterOnElement)) {
    showTemporaryTooltip(
      el,
      `${i18next.t("installed_individually")}`
    );
    return;
  }
  if (realImg.linkedElementId && realImg.linkedElementId !== el.id) {
    showTemporaryTooltip(el, `${i18next.t("no_more_connection")}`);
    return;
  }

  runInAction(() => {
    realImg.linkedElementId = el.id;
    realImg.linkSide = imgData.linkSide;
    realImg.targetSide = imgData.targetSide;
    imgData.linkedElementId = el.id;
    imgData.linkSide = imgData.linkSide;
    imgData.targetSide = imgData.targetSide;
    const user = $sw.serverState.UserData.user || "";
    updateLastModify(realImg, user);
    store.droppedImages = [...store.droppedImages];
    const imgEl = document.querySelector(`img[data-id="${imgData.id}"]`);
    if (imgEl) updateImageTooltip(imgEl as HTMLImageElement, realImg);
  });


  if (realImg.propertysubtypes && realImg.setvalue) {
    const subtype = realImg.propertysubtypes.toLowerCase();
    const val = realImg.setvalue;
    if (el[subtype] !== null) {
      el.attributeChangedCallback(subtype, el[subtype], val, null, {forced: true});
    }
  }

  const imgElement = document.querySelector(`img[data-id="${imgData.id}"]`) as HTMLImageElement;
  if (imgElement) {
    removeConnectionLine(imgElement);
    showConnectionLine(imgElement, realImg);
  }
}

export function showTemporaryTooltip(target: HTMLElement, message: string) {
  const tooltip = document.createElement("div");
  tooltip.className = "mark-tooltip-error";
  tooltip.textContent = message;

  Object.assign(tooltip.style, {
    position: "absolute",
    backgroundColor: "#f44336",
    color: "#fff",
    padding: "5px 10px",
    borderRadius: "4px",
    fontSize: "13px",
    zIndex: "9999",
    top: `${target.getBoundingClientRect().top - 30}px`,
    left: `${target.getBoundingClientRect().left}px`,
    pointerEvents: "none",
    whiteSpace: "nowrap",
    boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
  });

  document.body.appendChild(tooltip);

  setTimeout(() => {
    tooltip.remove();
  }, 1000);
}

export function clearAllSelections() {
  document.querySelectorAll<HTMLElement>(".dropped-image.selected").forEach(el => {
    el.classList.remove("selected");
    el.style.border = "";
    el.parentElement?.querySelector(".anchor-point")?.remove();
  });
  if (activeClickImage) {
    const prevId = activeClickImage.dataset.id;
    const prevControlId = activeClickImage.dataset.controlId;

    if (prevId && prevControlId) {
      const store = getStoreByControlId(prevControlId);
      const prevImg = store?.droppedImages.find(i => i.id === prevId);
      if (prevImg?.linkedElementId) {
        const linked = document.getElementById(prevImg.linkedElementId);
        if (linked) {
          linked.style.outline = "";
          linked.style.outlineOffset = "";
        }
      }
    }
    removeConnectionLineForce(activeClickImage);
    (activeClickImage as any)._forceShowConnection = false;
    (activeClickImage as any)._hoverConnection = false;
    activeClickImage = null;
  }
}

export function setImageComment(imgData: DroppedImage, text: string, user: string) {
  const store = getStoreByControlId(imgData.controlId);
  if (!store) return;

  const realImg = store.droppedImages.find(i => i.id === imgData.id);
  if (!realImg) return;

  runInAction(() => {
    realImg.comment = text || "";
    imgData.comment = text || "";
    updateLastModify(realImg, user)
    updateLastModify(imgData, user)

    store.droppedImages = [...store.droppedImages];
  });

  const imgEl = Array.from(
    document.querySelectorAll<HTMLImageElement>(`img[data-id="${realImg.id}"]`)
  ).find(el => {
    const win = el.closest("ms-window");
    const winId =
      win?.getAttribute("data-window-id")
      ?? win?.id
      ?? null;

    return winId === realImg.windowId;
  });
  if (imgEl) {
    updateImageTooltip(imgEl, realImg);
  }
}

export function updateImageTooltip(img: HTMLImageElement, realImg: DroppedImage) {
  const info: string[] = [];

  if (realImg.comment && realImg.comment.trim()) {
    info.push(`${i18next.t("mark_user_comment")}: ${realImg.comment}`);
  } else {
    info.push(`${i18next.t("mark_user_comment")}: —`);
  }

  if (realImg.commentAuthor) {
    info.push(`${i18next.t("mark_username")}: ${realImg.commentAuthor}`);
  } else {
    info.push(`${i18next.t("mark_username")}: —`)
  }

  if (realImg.commentDate) {
    info.push(`${i18next.t("mark_last_change")}: ${new Date(realImg.commentDate).toLocaleString("ru-RU")}`);
  }

  img.title = info.join("\n");
}

let dropHandlerInitialized = false;

export function setupDragAndDrop(hostElement: HTMLElement) {
  if (dropHandlerInitialized) return;
  dropHandlerInitialized = true;

  document.addEventListener("dragover", (e) => e.preventDefault());

  document.addEventListener("drop", (e: DragEvent) => {
    e.preventDefault();
    const dropTarget = document.elementFromPoint(e.clientX, e.clientY);
    if (dropTarget?.closest(".mark-modal")) return;
    let realHost = document.querySelector("ms-markcontrol.opened") as HTMLElement | null;
    if (!realHost) realHost = dropTarget?.closest("ms-markcontrol") as HTMLElement | null;
    if (!realHost) realHost = hostElement;
    if ($pm.hasPermissions && !$pm.checkAllow($pm.getEntryFromElement(realHost, "ManageMarksAndPosters"))) return;

    const imgData = handleImageDrop(e, markStore.marks, realHost);
    if (!imgData) return;

    const img = createImageElement(imgData);
    const store = getStoreByControlId(imgData.controlId);

    makeImageDraggable(img, imgData, store);
    attachImageLogic(img, imgData, selectImage.bind(realHost), realHost);
  });
}